import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useKryptonClient } from './useKryptonClient';
import './smartform-embed.css';

/**
 * Wrapper React autour du SmartForm Krypton (Lyra / SogeCommerce).
 *
 * En mode kr-smart-form, Krypton auto-monte le formulaire via setFormConfig,
 * inutile d'appeler addForm()/showForm().
 */
export const SmartFormEmbed = ({ formToken, publicKey, endpoint, onPaid, onError }) => {
    const containerRef = useRef(null);
    const { ready, error } = useKryptonClient({ publicKey, endpoint });

    const onPaidRef = useRef(onPaid);
    const onErrorRef = useRef(onError);
    onPaidRef.current = onPaid;
    onErrorRef.current = onError;

    useEffect(() => {
        if (!ready || !formToken || !window.KR || !containerRef.current) return;

        let cancelled = false;

        (async () => {
            try {
                window.KR.onSubmit((event) => {
                    if (event?.clientAnswer?.orderStatus === 'PAID') {
                        onPaidRef.current();
                    }
                    return false;
                });

                window.KR.onError((event) => {
                    // Krypton fire l'event en plein milieu de sa propre mutation
                    // du DOM (insertion du toast/banner d'erreur dans le shadow
                    // form). Si on appelle setState synchroniquement ici, la
                    // reconciliation React intervient pendant que Krypton n'a
                    // pas fini → DOMException "insertBefore: Child not a child".
                    // queueMicrotask repousse au tick suivant, après que Krypton
                    // a stabilisé son DOM.
                    const message = event?.errorMessage || 'Erreur du module de paiement.';
                    queueMicrotask(() => onErrorRef.current?.(message));
                });

                await window.KR.setFormConfig({ publicKey, formToken });
                if (cancelled) return;
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[SmartFormEmbed] init error:', e);
                onErrorRef.current?.(e?.message || 'Erreur lors de l\'initialisation du SmartForm.');
            }
        })();

        return () => { cancelled = true; };
    }, [ready, formToken, publicKey]);

    if (error) {
        return (
            <div className="alert alert-danger">
                {error.message}
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            id="smartform-embed-container"
            className="kr-smart-form"
            kr-card-form-expanded=""
        />
    );
};

SmartFormEmbed.propTypes = {
    formToken: PropTypes.string.isRequired,
    publicKey: PropTypes.string.isRequired,
    endpoint: PropTypes.string,
    onPaid: PropTypes.func.isRequired,
    onError: PropTypes.func,
};
